// ###### IMPORTS
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/solid';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import SpinnerLoad from '../../moduleComponents/SpinnerLoad';
import { listPlannerGoogleRemoteCalendars } from '../../moduleServices/planner_google_api';
import {
  DEFAULT_CALENDAR_TYPE,
  PLANNER_CALENDAR_TYPE_OPTIONS,
} from '../../moduleServices/planner_calendar_types';

// ###### COMPONENT
const DEFAULT_TIMEZONE = 'America/Sao_Paulo';

/** Label next to Google toggle (shared across states). */
const GOOGLE_SYNC_LABEL = 'Turn on to sync with Google';

/** Fallback timezone list when the browser lacks `Intl.supportedValuesOf('timeZone')`. */
const IANA_FALLBACK_TIMEZONES = [
  'UTC',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'America/Bogota',
  'America/Caracas',
  'America/Chicago',
  'America/Denver',
  'America/Fortaleza',
  'America/Lima',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/Montevideo',
  'America/New_York',
  'America/Phoenix',
  'America/Santiago',
  'America/Toronto',
  'America/Vancouver',
  'Europe/Lisbon',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Paris',
  'Asia/Dubai',
  'Asia/Tokyo',
  'Australia/Sydney',
];

function getAllIanaTimezones() {
  if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
    try {
      const z = Intl.supportedValuesOf('timeZone');
      if (Array.isArray(z) && z.length) return z.slice().sort((a, b) => a.localeCompare(b));
    } catch {
      /* ignore */
    }
  }
  return IANA_FALLBACK_TIMEZONES.slice().sort((a, b) => a.localeCompare(b));
}

const CalendarForm = ({ calendar = null, onSave, onCancel, isSubmitting = false }) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [ianaTimezone, setIanaTimezone] = useState(DEFAULT_TIMEZONE);
  const [googleCalendarId, setGoogleCalendarId] = useState('');
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [calendarType, setCalendarType] = useState(DEFAULT_CALENDAR_TYPE);
  const [googleRemoteCalendars, setGoogleRemoteCalendars] = useState([]);
  const [googleListLoading, setGoogleListLoading] = useState(false);
  const [googleListError, setGoogleListError] = useState('');

  const baseTimezoneOptions = useMemo(() => getAllIanaTimezones(), []);

  const timezoneSelectOptions = useMemo(() => {
    if (!ianaTimezone.trim()) return baseTimezoneOptions;
    if (baseTimezoneOptions.includes(ianaTimezone)) return baseTimezoneOptions;
    return [...baseTimezoneOptions, ianaTimezone].sort((a, b) => a.localeCompare(b));
  }, [baseTimezoneOptions, ianaTimezone]);

  useEffect(() => {
    if (calendar) {
      setName(calendar.name || '');
      setColor(calendar.color || '#3B82F6');
      setIanaTimezone(calendar.iana_timezone || DEFAULT_TIMEZONE);
      setGoogleCalendarId(calendar.google_calendar_id || '');
      setSyncEnabled(!!calendar.sync_enabled);
      setCalendarType(
        calendar.calendar_type && PLANNER_CALENDAR_TYPE_OPTIONS.some((o) => o.value === calendar.calendar_type)
          ? calendar.calendar_type
          : DEFAULT_CALENDAR_TYPE,
      );
    } else {
      setName('');
      setColor('#3B82F6');
      setIanaTimezone(DEFAULT_TIMEZONE);
      setGoogleCalendarId('');
      setSyncEnabled(false);
      setCalendarType(DEFAULT_CALENDAR_TYPE);
    }
  }, [calendar]);

  const loadGoogleCalendars = useCallback(async () => {
    setGoogleListLoading(true);
    setGoogleListError('');
    try {
      const data = await listPlannerGoogleRemoteCalendars();
      setGoogleRemoteCalendars(Array.isArray(data?.calendars) ? data.calendars : []);
      if (data?.connected === false && data?.last_error) {
        setGoogleListError(data.last_error);
      }
    } catch {
      setGoogleRemoteCalendars([]);
    } finally {
      setGoogleListLoading(false);
    }
  }, []);

  const sortedGoogleChoices = useMemo(() => {
    const xs = googleRemoteCalendars.map((c) => ({
      id: c.id || '',
      summary: c.summary || c.id || 'Untitled',
      primary: c.primary,
    })).filter((c) => c.id);
    xs.sort((a, b) => {
      if (a.primary && !b.primary) return -1;
      if (!a.primary && b.primary) return 1;
      return (a.summary || '').localeCompare(b.summary || '', undefined, { sensitivity: 'base' });
    });
    const trimmed = googleCalendarId.trim();
    const idSet = new Set(xs.map((x) => x.id));
    if (trimmed && !idSet.has(trimmed)) {
      xs.unshift({ id: trimmed, summary: trimmed, primary: undefined });
    }
    return xs;
  }, [googleRemoteCalendars, googleCalendarId]);

  /** Remote Google list entries or calendar already persisted (may not be in the latest API shape). */
  const showGoogleCalendarSelect =
    !googleListLoading && sortedGoogleChoices.length > 0;

  /** Matches Google Card "Reload list" affordance */
  const reloadGoogleCalendarListButton = (
    <button
      type="button"
      onClick={() => void loadGoogleCalendars()}
      disabled={isSubmitting || googleListLoading}
      className="p-1 rounded hover:bg-gray-100 text-gray-600 disabled:opacity-40 flex-shrink-0"
      aria-label="Reload Google calendar list"
      title="Reload list"
    >
      <ArrowPathIcon className={`h-4 w-4 ${googleListLoading ? 'animate-spin' : ''}`} />
    </button>
  );

  /** Mesmo toggle visual que Fade past nas General options do Planner; label fixo. */
  const renderGoogleSyncToggle = () => (
    <div className="flex items-center gap-3 flex-shrink-0">
      <button
        type="button"
        role="switch"
        aria-checked={syncEnabled}
        aria-label={GOOGLE_SYNC_LABEL}
        disabled={isSubmitting}
        onClick={() => setSyncEnabled((v) => !v)}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full border border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:pointer-events-none ${
          syncEnabled ? 'bg-blue-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
            syncEnabled ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
      <span className="text-sm text-gray-500">{GOOGLE_SYNC_LABEL}</span>
    </div>
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      color,
      iana_timezone: ianaTimezone.trim() || DEFAULT_TIMEZONE,
      calendar_type: calendarType,
      google_calendar_id: googleCalendarId.trim(),
      sync_enabled: syncEnabled,
    });
  };

  const colorPresets = [
    '#3B82F6', // blue
    '#10B981', // green
    '#F59E0B', // yellow
    '#EF4444', // red
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#06B6D4', // cyan
    '#F97316', // orange
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-start">
        <div className="min-w-0 flex-1">
          <label className="mb-1 block text-sm font-medium text-gray-700">Calendar name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Calendar name"
            required
            disabled={isSubmitting}
          />
        </div>
        <div className="min-w-0 sm:w-[12rem] sm:flex-shrink-0">
          <label htmlFor="planner-cal-calendar-type" className="mb-1 block text-sm font-medium text-gray-700">
            Type
          </label>
          <select
            id="planner-cal-calendar-type"
            value={calendarType}
            onChange={(e) => setCalendarType(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isSubmitting}
          >
            {PLANNER_CALENDAR_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1">
          <label className="mb-2 block text-sm font-medium text-gray-700">Color</label>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="flex flex-wrap gap-2">
              {colorPresets.map((presetColor) => (
                <button
                  key={presetColor}
                  type="button"
                  onClick={() => setColor(presetColor)}
                  className={`h-8 w-8 rounded-full border-2 transition-all ${
                    color === presetColor ? 'scale-110 border-gray-800' : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: presetColor }}
                  disabled={isSubmitting}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-10 w-10 cursor-pointer rounded border border-gray-300"
                disabled={isSubmitting}
              />
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-20 rounded border border-gray-300 px-2 py-1 font-mono text-xs"
                placeholder="#3B82F6"
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <label htmlFor="planner-cal-iana-tz" className="mb-1 block text-sm font-medium text-gray-700">
            IANA timezone
          </label>
          <select
            id="planner-cal-iana-tz"
            value={ianaTimezone}
            onChange={(e) => setIanaTimezone(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isSubmitting}
          >
            {timezoneSelectOptions.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </div>
      </div>

      {googleListLoading ? (
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <label className="text-sm font-medium text-gray-700">Google calendar</label>
              {reloadGoogleCalendarListButton}
            </div>
            <div className="flex items-center gap-2 min-h-[38px] px-3 py-2 border border-gray-200 rounded-lg bg-gray-50">
              <SpinnerLoad size="small" />
              <span className="text-sm text-gray-600">Loading…</span>
            </div>
          </div>
          <div className="flex-shrink-0 sm:pb-[2px]">{renderGoogleSyncToggle()}</div>
        </div>
      ) : showGoogleCalendarSelect ? (
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <label htmlFor="planner-cal-google-id" className="text-sm font-medium text-gray-700">
                Google calendar (optional)
              </label>
              {reloadGoogleCalendarListButton}
            </div>
            <select
              id="planner-cal-google-id"
              value={sortedGoogleChoices.some((c) => c.id === googleCalendarId) ? googleCalendarId : ''}
              onChange={(e) => setGoogleCalendarId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              disabled={isSubmitting}
            >
              <option value="">None</option>
              {sortedGoogleChoices.map((c) => (
                <option key={c.id} value={c.id} title={c.id}>
                  {c.summary}
                  {c.primary ? ' (primary)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-shrink-0 sm:pb-[2px]">{renderGoogleSyncToggle()}</div>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-gray-700">Google calendar</span>
            {reloadGoogleCalendarListButton}
          </div>
          {googleListError ? (
            <p className="text-xs text-red-600 mb-2">{googleListError}</p>
          ) : null}
          {renderGoogleSyncToggle()}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="p-2 rounded-lg transition-colors text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !name.trim()}
          className="p-2 rounded-lg transition-colors bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {isSubmitting ? (
            <SpinnerLoad size="small" />
          ) : (
            <CheckIcon className="h-5 w-5" />
          )}
        </button>
      </div>
    </form>
  );
};

export default CalendarForm;
